import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import { FAQSection } from "@/components/FAQSection";
import { CONTACT_PAGE, CONTACT_FAQ } from "@/data/contactContent";

export default function ContactPage() {
  const C = CONTACT_PAGE;
  return (
    <div data-testid="contact-page" className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section data-testid="contact-hero" className="relative overflow-hidden bg-[#0b0b0c] hero-noise">
          <img src={C.hero.bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.86) 0%, rgba(2,2,2,0.62) 50%, rgba(2,2,2,0.86) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[420px] md:min-h-[460px] flex items-center justify-center pt-32 pb-14 md:pt-36 md:pb-16">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                {C.hero.eyebrow}
              </p>
              <h1
                data-testid="contact-hero-title"
                className="font-nunito font-extrabold text-white text-4xl sm:text-5xl leading-[1.08] tracking-tight"
              >
                {C.hero.titlePre}
                <span className="text-[#ee2e24]">{C.hero.titleAccent}</span>
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {C.hero.sub}
              </p>
            </div>
          </div>
        </section>

        {/* Info + Form */}
        <section data-testid="contact-main" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            {/* Direct lines */}
            <div className="lg:col-span-2">
              <h2 className="font-nunito text-2xl font-extrabold text-[#383838] heading-redul">{C.info.heading}</h2>
              <p className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{C.info.body}</p>
              <div className="mt-8 space-y-4">
                {C.info.items.map((item) => {
                  const Icon = item.icon;
                  const inner = (
                    <>
                      <span className="shrink-0 h-11 w-11 rounded-[10px] bg-[#ee2e24]/[0.08] flex items-center justify-center">
                        <Icon size={19} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </span>
                      <div>
                        <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8a8a8a]">{item.label}</p>
                        <p className="mt-0.5 font-nunito text-[15px] font-extrabold text-[#383838]">{item.value}</p>
                      </div>
                    </>
                  );
                  const cls =
                    "flex items-center gap-4 rounded-[12px] border border-[#ececec] bg-[#fafafa] p-4 hover:border-[#ee2e24]/30 transition-colors duration-150";
                  return item.href ? (
                    <a key={item.label} href={item.href} data-testid={`contact-info-${item.label.toLowerCase().replace(/\s/g, "-")}`} className={cls}>
                      {inner}
                    </a>
                  ) : (
                    <div key={item.label} data-testid={`contact-info-${item.label.toLowerCase().replace(/\s/g, "-")}`} className={cls}>
                      {inner}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form panel (Gravity Form lookalike — live WP uses [gravityform id=3]) */}
            <div className="lg:col-span-3">
              <div data-testid="contact-form-panel" className="rounded-[14px] bg-[#efeeee] p-7 md:p-8 shadow-[0_10px_30px_rgba(2,2,2,0.08)]">
                <h2 className="font-nunito text-xl font-extrabold text-[#383838]">{C.form.heading}</h2>
                <div className="mt-3 flex items-start gap-2 rounded-[10px] bg-white/70 border border-[#e0e0e0] p-3">
                  <Info size={14} className="text-[#ee2e24] shrink-0 mt-0.5" />
                  <p className="text-[12px] text-[#6e6e6e] leading-snug">{C.form.note}</p>
                </div>
                <form className="mt-6 space-y-4" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {C.form.fields.filter((f) => f.type !== "textarea").map((f) => (
                      <div key={f.id} className={f.id === "subject" || f.id === "linkedin" ? "sm:col-span-1" : ""}>
                        <Label htmlFor={`contact-${f.id}`} className="font-nunito text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#6e6e6e]">
                          {f.label}
                        </Label>
                        <Input
                          id={`contact-${f.id}`}
                          type={f.type}
                          placeholder={f.placeholder}
                          data-testid={`contact-form-${f.id}-input`}
                          className="mt-1.5 bg-white border-[#e0e0e0] focus-visible:ring-[#ee2e24]/30"
                        />
                      </div>
                    ))}
                  </div>
                  {C.form.fields.filter((f) => f.type === "textarea").map((f) => (
                    <div key={f.id}>
                      <Label htmlFor={`contact-${f.id}`} className="font-nunito text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#6e6e6e]">
                        {f.label}
                      </Label>
                      <Textarea
                        id={`contact-${f.id}`}
                        rows={5}
                        placeholder={f.placeholder}
                        data-testid={`contact-form-${f.id}-input`}
                        className="mt-1.5 bg-white border-[#e0e0e0] focus-visible:ring-[#ee2e24]/30"
                      />
                    </div>
                  ))}
                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    className="w-full inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-6 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
                  >
                    Send Message
                  </button>
                  <p className="text-center text-[11px] text-[#9a9a9a]">
                    Demo form — the deployed WordPress page uses your live Gravity Form with CAPTCHA.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ + schema */}
        <FAQSection heading={CONTACT_FAQ.heading} items={CONTACT_FAQ.items} injectSchema />

        {/* CTA */}
        <section data-testid="contact-cta" className="bg-[#fafafa] border-t border-[#f1f1f1] py-14 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-nunito text-2xl font-extrabold text-[#383838]">{C.cta.heading}</h2>
            <p className="mt-4 text-[15px] text-[#6e6e6e] leading-relaxed">{C.cta.sub}</p>
            <Link
              to="/#visibility-audit"
              data-testid="contact-cta-primary-button"
              className="mt-7 inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] transition-colors duration-150"
            >
              {C.cta.primaryText}
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
